/**
 * Wallet & Referrals - one page, six views.
 *
 * Tabs rather than six sidebar entries, matching ManagementPage: these views
 * share a date filter and are read in sequence during a reconciliation, so
 * splitting them across routes would mean re-picking the period each time.
 *
 * The tab lives in the query string so an operator can bookmark or share
 * "the ledger, last 30 days" - which is exactly what gets pasted into a message
 * when something looks wrong.
 */
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { PageHeader, SegmentedControl } from '../../features/b2b/components/primitives'
import { userHasPermission } from '../../lib/permissions'
import { useAuthStore } from '../../store/authStore'
import DateRangePicker from './DateRangePicker'
import WalletOverviewTab from './WalletOverviewTab'
import WalletLedgerTab from './WalletLedgerTab'
import WalletCustomersTab from './WalletCustomersTab'
import TopupsTab from './TopupsTab'
import ReferralsTab from './ReferralsTab'
import ReconciliationTab from './ReconciliationTab'
import CampaignsTab from './CampaignsTab'
import type { DateRangeFilter } from '../../services/walletApi'

type WalletTab =
  | 'overview'
  | 'ledger'
  | 'balances'
  | 'topups'
  | 'referrals'
  | 'reconciliation'
  | 'campaigns'

const TABS: Array<{ value: WalletTab; label: string }> = [
  { value: 'overview', label: 'Overview' },
  { value: 'ledger', label: 'Ledger' },
  { value: 'balances', label: 'Balances' },
  { value: 'topups', label: 'Top-ups' },
  { value: 'referrals', label: 'Referrals' },
  { value: 'reconciliation', label: 'Reconciliation' },
  { value: 'campaigns', label: 'Offers & campaigns' },
]

/** Views that ignore the date filter - hiding it prevents "why did nothing change?" */
const UNDATED_TABS = new Set<WalletTab>(['balances', 'campaigns'])

export default function WalletPage() {
  const user = useAuthStore((state) => state.user)
  const canManage = userHasPermission(user, 'wallet.manage')

  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as WalletTab | null
  const [tab, setTab] = useState<WalletTab>(
    TABS.some((entry) => entry.value === tabParam) ? (tabParam as WalletTab) : 'overview',
  )
  const [filter, setFilter] = useState<DateRangeFilter>({ range: 'last_30_days' })

  useEffect(() => {
    // `replace` so tab switching does not fill the operator's back button with
    // every view they glanced at on the way to the one they wanted.
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      next.set('tab', tab)
      return next
    }, { replace: true })
  }, [tab, setSearchParams])

  const visibleTabs = useMemo(
    // Editing what a campaign gives away is a different level of trust from
    // reading the ledger, so the tab is not shown to someone who cannot act on it.
    () => TABS.filter((entry) => entry.value !== 'campaigns' || canManage),
    [canManage],
  )

  return (
    <div style={{ padding: '24px 32px 48px' }}>
      <PageHeader
        title="Wallet & Referrals"
        subtitle="Every rupee in customer wallets, where it came from, and where it went."
      />

      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: '16px', flexWrap: 'wrap', marginBottom: '20px',
        }}
      >
        <SegmentedControl<WalletTab> value={tab} options={visibleTabs} onChange={setTab} size="sm" />
        {UNDATED_TABS.has(tab) ? null : <DateRangePicker value={filter} onChange={setFilter} />}
      </div>

      {tab === 'overview' ? <WalletOverviewTab filter={filter} /> : null}
      {tab === 'ledger' ? <WalletLedgerTab filter={filter} /> : null}
      {tab === 'balances' ? <WalletCustomersTab /> : null}
      {tab === 'topups' ? <TopupsTab filter={filter} /> : null}
      {tab === 'referrals' ? <ReferralsTab filter={filter} /> : null}
      {tab === 'reconciliation' ? <ReconciliationTab filter={filter} /> : null}
      {tab === 'campaigns' && canManage ? <CampaignsTab /> : null}
    </div>
  )
}
